// src/App.js
import { useEffect, useState } from 'react';
import './App.css';
import {
  createBrowserRouter,
  createHashRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { initializeAPI } from './utils/Http';

import Homepage from './pages/Homepage';
import Profile from './components/UserManagement/Profile';
import NavigationBar from './components/common/NavigationBar';
import "../src/components/common/NavigationBar.css";
import "./index.css";
import EnterpriseLogin from './components/user_management/Login';
import CourseDetails from './pages/CourseDetailsPage/components/CourseDetails';
import AllCourses from './pages/AllCoursesPage';
import MyEnrolledCourses from './pages/MyEnrolledCourses';
import CourseContentPage from './components/CourseContentPage/CourseContentPage';
import CourseEditor from './components/course_management/CourseEditor/CourseEditor';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Footer from './components/common/Footer';
import ContactPage from './pages/ContactPage';
import AboutUsPage from './pages/AboutUsPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AdminMessagesList from "./pages/AdminMessagesList";
import AdminDashboard from './pages/AdminDashboard';
import ContactAdminPage from './pages/ContactAdminPage';
import EnrollmentRouteGuard from './pages/CourseDetailsPage/components/EnrollmentRouteGuard';
import SignUp from './pages/SignUp';

// ── Leadership ───────────────────────────────────────────
import LeadershipPage from './pages/LeadershipPage';
import useLeadershipPageStatus from "./hooks/useLeadershipPageStatus";

// ── Store (public) ───────────────────────────────────────
import StoresPage from './pages/storePages/StoresPage';
import StoreCategoryPage from './pages/storePages/StoreCategoryPage';
import StoreProductPage from './pages/storePages/ProductDetailPage';

// ── Store (cart / checkout / order) ──────────────────────
import CheckoutPage from './pages/storePages/CheckoutPage';
import OrdersPage from './pages/storePages/OrdersPage';

// ── Manage Store (admin) ─────────────────────────────────
import ManageStoreLayout from './pages/storePages/manageStore/ManageStoreLayout';
import ManageStoreRoot from './pages/storePages/manageStore/ManageStoreRoot';
import ManageStoreOrders from './pages/storePages/manageStore/ManageStoreOrders';
import ManageStorePayment from './pages/storePages/manageStore/ManageStorePayment';
import ManageCategoryPage from './pages/storePages/manageStore/ManageCategoryPage';
import CategoryFormPage from './pages/storePages/manageStore/CategoryFormPage';
import ProductFormPage from './pages/storePages/manageStore/ProductFormPage';
import ManageStoreProducts from './pages/storePages/manageStore/ManageStoreProducts';

// ── Cart drawer ──────────────────────────────────────────
import CartDrawer from './components/store/CartDrawer';

// ── Detect environment (safe + reliable) ────────────────
const isElectron =
  typeof window !== 'undefined' &&
  (window.electronAPI?.isElectron === true ||
   navigator.userAgent.toLowerCase().includes('electron'));



// ── Root layout ──────────────────────────────────────────
function RootLayout() {
  return (
    <>
      <Outlet />
      <CartDrawer />
    </>
  );
}

// ── Guard ────────────────────────────────────────────────
function RequireLeadershipEnabled({ children }) {
  const { enabled, loading } = useLeadershipPageStatus();

  if (loading) {
    return <div className="app-loading-text">Loading…</div>;
  }

  if (!enabled) return <Navigate to="/" replace />;

  return children;
}

// ── Routes ───────────────────────────────────────────────
const routes = [
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: (
          <>
            <NavigationBar />
            <Homepage />
          </>
        ),
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/login',
        element: <EnterpriseLogin />,
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/orders',
        element: (
          <>
            <NavigationBar />
            <OrdersPage />
          </>
        ),
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/admin',
        element: (
          <>
            <NavigationBar />
            <AdminDashboard />
          </>
        ),
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/profile',
        element: (
          <>
            <NavigationBar />
            <Profile />
          </>
        ),
      },
      {
        path: '/store',
        element: (
          <>
            <NavigationBar />
            <StoresPage />
          </>
        ),
      },
      {
        path: '/store/category/:slug',
        element: (
          <>
            <NavigationBar />
            <StoreCategoryPage />
          </>
        ),
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/store/product/:slug',
        element: (
          <>
            <NavigationBar />
            <StoreProductPage />
          </>
        ),
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/checkout',
        element: (
          <>
            <NavigationBar />
            <CheckoutPage />
          </>
        ),
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/manage-store',
        element: (
          <>
            <NavigationBar />
            <ManageStoreLayout />
          </>
        ),
        errorElement: <p>404 Not found</p>,
        children: [
          { index: true, element: <ManageStoreRoot /> },
          { path: 'products', element: <ManageStoreProducts /> },
          { path: 'orders', element: <ManageStoreOrders /> },
          { path: 'payment', element: <ManageStorePayment /> },
          { path: 'category/new', element: <CategoryFormPage /> },
          { path: 'category/:slug', element: <ManageCategoryPage /> },
          { path: 'category/:slug/edit', element: <CategoryFormPage /> },
          { path: 'product/new', element: <ProductFormPage /> },
          { path: 'product/:slug/edit', element: <ProductFormPage /> },
        ],
      },
      {
          path: '/course/:slug',
          element: (
            <>
              <NavigationBar />
              <EnrollmentRouteGuard>
                <CourseDetails />
              </EnrollmentRouteGuard>
            </>
          ),
        },
      {
        path: '/courses',
        element: (
          <>
            <NavigationBar />
            <AllCourses isProject={false} />
          </>
        ),
      },
      {
        path: '/projects',
        element: (
          <>
            <NavigationBar />
            <AllCourses isProject={true} />
          </>
        ),
      },
      {
        path: '/content/:slug',
        element: (
          <>
            <NavigationBar />
            <CourseContentPage />
          </>
        ),
      },
      {
        path: '/course-editor/',
        element: (
          <>
            <NavigationBar />
            <CourseEditor />
          </>
        ),
      },
      {
        path: '/signup/',
        element: (
          <>
            <NavigationBar />
            <SignUp />
          </>
        ),
      },
      {
        path: '/instructor',
        element: (
          <>
            <NavigationBar />
            <Footer />
          </>
        ),
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/enrolled-courses',
        element: (
          <>
            <NavigationBar />
            <MyEnrolledCourses />
          </>
        ),
      },
      {
        path: '/contact',
        element: (
          <>
            <NavigationBar />
            <ContactPage />
          </>
        ),
      },
      {
        path: '/about',
        element: (
          <>
            <NavigationBar />
            <AboutUsPage />
          </>
        ),
      },
      {
        path: '/leadership',
        element: (
          <>
            <NavigationBar />
            <RequireLeadershipEnabled>
              <LeadershipPage />
            </RequireLeadershipEnabled>
          </>
        ),
        errorElement: <p>404 Not found</p>,
      },
      {
        path: '/privacy',
        element: (
          <>
            <NavigationBar />
            <PrivacyPolicy />
          </>
        ),
      },
      {
        path: '/terms',
        element: (
          <>
            <NavigationBar />
            <TermsOfService />
          </>
        ),
      },
      {
        path: '/course/:courseId/support',
        element: (
          <>
            <NavigationBar />
            <ContactAdminPage />
          </>
        ),
      },
      {
        path: '/admin/messages',
        element: (
          <>
            <NavigationBar />
            <AdminMessagesList />
          </>
        ),
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
];

// ── Create router based on environment ───────────────────
const router = isElectron
  ? createHashRouter(routes)
  : createBrowserRouter(routes);

// ── App ──────────────────────────────────────────────────
function App() {
  const [apiReady, setApiReady] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        
        await initializeAPI();
        
        setApiReady(true);
      } catch (err) {
        
        setInitError(err.message || 'Failed to initialize API');
      }
    };
    init();
  }, []);

  // ── Loading state ────────────────────────────────────
  if (!apiReady && !initError) {
    return (
      <div className="app-loading-screen">
        <div className="app-loading-spinner" />
        <h2 className="app-loading-title">Loading Hayducate…</h2>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────
  if (initError) {
    return (
      <div className="app-error-screen">
        <h2 className="app-error-title">⚠️ Initialization Failed</h2>
        <p className="app-error-message">{initError}</p>
        <button
          className="app-error-retry"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Render app ───────────────────────────────────────
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <RouterProvider router={router} />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;